import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import config from "../../../config";
import User from "../../../assets/img/icons/user3.png";
import { companyID, getEmployeeLocalDetails } from "./../../../auth";
import api from "../../../api/axios";
import { getFile } from "../../../utils/getFile";
import { useTranslation } from "react-i18next";
import FilterComponent from "../../../components/FilterComponent";
import TooltipWrapper from "../../../utils/TooltipWrapper";
import { getDepartmentById } from "../../../services/departmentService";
import { encrypt } from "../../../utils/crypto";

const EmployeeOnboardingList = () => {
  const { t } = useTranslation();
  const [paginationValue, setPaginationValue] = useState(10);
  const [employeeData, setEmployeeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageMap, setImageMap] = useState({});
  const [currentEmployees, setCurrentEmployees] = useState([]);
  const [filterOptions, setFilterOptions] = useState([
    {
      key: "Pending",
      label: "onboarding",
      value: ["PENDING"],
      category: "Onboarding status",
    },
    {
      key: "Completed",
      label: "onboarding",
      value: ["COMPLETED"],
      category: "Onboarding status",
    },
    {
      key: "All",
      label: "onboarding",
      value: null,
      category: "Onboarding status",
    },
  ]);

  const employeeDetails = getEmployeeLocalDetails();

  const loginEmployeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const fetchCompanyEmployees = async (
    params = { onboarding: ["PENDING"] }
  ) => {
    if (
      !employeeDetails ||
      !employeeDetails.employee_role ||
      (employeeDetails.employee_role != 1 &&
        !employeeDetails.working_departments?.length)
    ) {
      setEmployeeData([]);
      return;
    }

    try {
      let finalParams = { ...params };
      if (
        employeeDetails.employee_role != 1 &&
        employeeDetails.working_departments?.length
      ) {
        if (!("department_id" in finalParams))
          finalParams.department_id = employeeDetails.working_departments;
      }

      const queryParams = new URLSearchParams();
      Object.entries(finalParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val !== null && val !== undefined && val !== "") {
              queryParams.append(key, val);
            }
          });
        } else {
          queryParams.append(key, value);
        }
      });
      queryParams.append("status", 1);

      const response = await api.get(
        `employees/employeeDetails/${company_id}?${queryParams.toString()}`
      );

      if (!Array.isArray(response?.data)) {
        console.error("Invalid data format received:", response);
        setEmployeeData([]);
        return;
      }

      setEmployeeData(response.data ?? []);
    } catch (error) {
      console.error("Error fetching company employees:", error);
      setEmployeeData([]);
    } finally {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    if (company_id) {
      fetchCompanyEmployees();
    }
  }, []);

  const employmentStatusColor = {
    probationary: "secondary",
    permanent: "success",
    terminated: "danger",
    separated: "danger",
    intern: "info",
    on_notice: "warning",
  };

  const columns = [
    {
      title: t("Name"),
      dataIndex: "name",
      render: (text, record) => {
        const encryptedEmployeeId = encrypt(record.id);
        return (
          <span className="table-avatar">
            <Link
              to={`../employees/profile/${encryptedEmployeeId}`}
              className="avatar"
            >
              <span>
                <img
                  alt=""
                  src={imageMap[record.id] || User}
                  style={{ borderRadius: "50%" }}
                />
              </span>
            </Link>
            <Link to={`../employees/profile/${encryptedEmployeeId}`}>
              <span>
                {record?.first_name} {record?.middle_name} {record?.last_name}
              </span>
            </Link>
          </span>
        );
      },
    },

    {
      title: t("Email"),
      dataIndex: "official_email",
    },

    {
      title: t("Mobile"),
      dataIndex: "number",
    },
    {
      title: t("Department"),
      dataIndex: "department_name",
    },
    {
      title: t("Designation"),
      dataIndex: "designation_name",
    },
    {
      title: t("Employment status"),
      dataIndex: "employment_status",
      render: (text, record) => {
        const employmentStatus = record.employment_status || "-";
        const statusColor =
          employmentStatusColor[record?.employment_status] || "secondary";
        return (
          <span className={`badge bg-${statusColor}`}>
            {employmentStatus
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")}
          </span>
        );
      },
    },
    {
      title: t("Status"),
      render: (text, record) => {
        const status = record.status == "1";
        return (
          <span className={`badge bg-${status ? "success" : "danger"}`}>
            {status ? t("Active") : t("Inactive")}
          </span>
        );
      },
    },
    {
      title: t("Onboarding Status"),
      render: (text, record) => {
        const color =
          record.onboarding_status === "PENDING" ? "warning" : "success";
        return (
          <span className={`badge bg-${color}`}>
            {record.onboarding_status}
          </span>
        );
      },
    },
    {
      title: t("Action"),
      render: (text, record) => {
        const encryptedEmployeeId = encrypt(record.id);
        const deactivate = record?.status == "1" ? false : true;
        let label, iconClass;

        switch (record.onboarding_status) {
          case "PENDING":
            label = "Not started";
            iconClass = "fa-solid fa-clock text-warning me-2";
            break;
          case "COMPLETED":
            label = "Completed";
            iconClass = "fa-solid fa-check-circle text-success me-2";
            break;
          default:
            label = "Not started";
            iconClass = "fa-solid fa-clock text-warning me-2";
        }

        return (
          <div className="d-flex gap-2 mt-2">
            <Link
              className="d-flex align-items-center"
              to={`../employee-onboarding-details/${encryptedEmployeeId}`}
              aria-label={`View onboarding details for ${label} status`}
            >
              {iconClass && <i className={iconClass} title={label}></i>}
            </Link>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const startIndex = (currentPage - 1) * paginationValue;
    const endIndex = startIndex + paginationValue;
    const employees = employeeData?.slice(startIndex, endIndex);

    setCurrentEmployees(employees);
  }, [employeeData, currentPage]);

  useEffect(() => {
    const fetchImages = async () => {
      const newImageMap = {};
      for (const employee of currentEmployees) {
        if (employee.photo) {
          const imageUrl = await getFile(
            `${config.IMAGE_URL}${employee.photo}`
          );
          if (imageUrl) {
            newImageMap[employee.id] = imageUrl;
          }
        }
      }
      setImageMap(newImageMap);
    };

    fetchImages();
  }, [currentEmployees]);

  const fetchDepartments = async () => {
    try {
      const departments = await getDepartmentById(company_id);
      if (!departments?.result?.length) {
        return;
      }
      const mappedDepartments = departments.result
        .filter((dept) => {
          if (
            employeeDetails.employee_role != 1 &&
            employeeDetails.working_departments?.length
          ) {
            return employeeDetails?.working_departments?.includes(dept.id);
          } else if (employeeDetails.employee_role == 1) {
            return true;
          } else {
            return false;
          }
        })
        .map((dept) => ({
          key: dept.department_name,
          label: "department_id",
          value: dept.id,
          category: "Department",
        }));

      setFilterOptions((prevOptions) => [...prevOptions, ...mappedDepartments]);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  return (
    <div className="page-wrapper">
      {/* Page Content */}
      <div className="content container-fluid">
        {/* Page Header */}
        <Breadcrumbs
          maintitle={t("Onboarding")}
          title={t("Dashboard")}
          subtitle={t("Onboarding")}
        />
        {/* /Page Header */}
        {/* <EmployeeListFilter /> */}
        <div className="d-flex justify-content-end mb-2">
          <TooltipWrapper tooltipText="Filter">
            <FilterComponent
              filterOptions={filterOptions}
              onApplyFilter={(selectedFilter) => {
                const queryParams = Object.entries(selectedFilter)?.length
                  ? selectedFilter
                  : {
                      onboarding: ["PENDING"],
                    };
                fetchCompanyEmployees(queryParams);
              }}
              filterLabel="Filter Employees"
              resetLabel="Reset Filter"
              applyLabel="Apply"
            />
          </TooltipWrapper>
        </div>

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={employeeData}
            rowKey={(record) => record.id}
            pagination={{ pageSize: paginationValue }}
            scroll={{ x: true }}
            tableLayout="auto"
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeOnboardingList;
